import type { Immutable } from 'immer';

import { ApprovalsReviewStatus, ApprovalWorkflowExecutionStatus } from '#utils/approvals/constants';
import type { ApprovalProgressStatusKey, ResourceTypeName } from '#utils/approvals/types';

export type ApprovalRequestSummariesFiltersPlain = {
  offset?: number;
  query?: string;
  assignedTo?: string | string[];
  tags?: string[];
  requestedBy?: string;
  status?: ApprovalProgressStatusKey | ApprovalProgressStatusKey[];
  projectKey?: string;
  resourceKind?: ResourceTypeName;
  resourceId?: string;
};

export type ApprovalRequestSummariesFilters = Immutable<ApprovalRequestSummariesFiltersPlain>;

export function parseApprovalsSearchParamsFromBrowser(searchParams: URLSearchParams) {
  const filters: ApprovalRequestSummariesFiltersPlain = {};
  if (searchParams.has('offset')) {
    filters.offset = Number(searchParams.get('offset'));
  }
  if (searchParams.has('query')) {
    filters.query = searchParams.get('query') ?? undefined;
  }
  if (searchParams.has('assignedTo')) {
    // Handle both single value and array of values
    const assignedToValues = searchParams.getAll('assignedTo');
    if (assignedToValues.length === 1) {
      filters.assignedTo = assignedToValues[0];
    } else if (assignedToValues.length > 1) {
      filters.assignedTo = assignedToValues;
    }
  }
  if (searchParams.has('tags')) {
    filters.tags = searchParams.getAll('tags');
  }
  if (searchParams.has('requestedBy')) {
    filters.requestedBy = searchParams.get('requestedBy') ?? undefined;
  }
  if (searchParams.has('status')) {
    // Handle both single value and array of values
    const statusValues = searchParams.getAll('status');
    if (statusValues.length === 1) {
      filters.status = statusValues[0] as ApprovalProgressStatusKey;
    } else if (statusValues.length > 1) {
      filters.status = statusValues as ApprovalProgressStatusKey[];
    }
  }
  if (searchParams.has('resourceKind')) {
    filters.resourceKind = searchParams.get('resourceKind') as ResourceTypeName;
  }

  return filters;
}

export function seralizeApprovalsSearchParamsToBrowser(filters: ApprovalRequestSummariesFiltersPlain) {
  const params = new URLSearchParams();
  if (filters.offset) {
    params.set('offset', filters.offset.toString());
  }
  if (filters.query) {
    params.set('query', filters.query);
  }
  if (filters.assignedTo) {
    if (Array.isArray(filters.assignedTo)) {
      filters.assignedTo.forEach((assignedTo) => params.append('assignedTo', assignedTo));
    } else {
      params.set('assignedTo', filters.assignedTo);
    }
  }
  if (filters.tags) {
    filters.tags.forEach((tag) => params.append('tags', tag));
  }
  if (filters.requestedBy) {
    params.set('requestedBy', filters.requestedBy);
  }
  if (filters.status) {
    if (Array.isArray(filters.status)) {
      filters.status.forEach((status) => params.append('status', status));
    } else {
      params.set('status', filters.status);
    }
  }
  if (filters.resourceKind) {
    params.set('resourceKind', filters.resourceKind);
  }

  return params;
}

export function makeApprovalRequestDashboardQueryParams(
  appliedFilters: ApprovalRequestSummariesFilters | ApprovalRequestSummariesFiltersPlain,
) {
  const params: { offset?: number; filter?: string } = {};

  const { offset, ...otherAppliedFilters } = appliedFilters;

  if (offset) {
    params.offset = offset;
  }

  const filters: string[] = [];
  if (Object.values(otherAppliedFilters).some(Boolean)) {
    if (appliedFilters.query) {
      filters.push(`query:${appliedFilters.query}`);
    }

    if (appliedFilters.assignedTo) {
      const assignedToArray = Array.isArray(appliedFilters.assignedTo)
        ? appliedFilters.assignedTo
        : [appliedFilters.assignedTo];
      filters.push(`notifyMemberIds anyOf ${JSON.stringify(assignedToArray)}`);
    }

    if (appliedFilters.tags) {
      filters.push(`flag.tags contains ${JSON.stringify(appliedFilters.tags)}`);
    }

    if (appliedFilters.requestedBy) {
      filters.push(`requestorId equals ${appliedFilters.requestedBy}`);
    }

    if (appliedFilters.status) {
      const statusArray: ApprovalProgressStatusKey[] = Array.isArray(appliedFilters.status)
        ? appliedFilters.status
        : [appliedFilters.status as ApprovalProgressStatusKey];

      // Map each status to the appropriate reviewStatus and status combinations
      const reviewStatuses: ApprovalsReviewStatus[] = [];
      const workflowStatuses: ApprovalWorkflowExecutionStatus[] = [];

      statusArray.forEach((status) => {
        switch (status) {
          case 'PENDING':
            reviewStatuses.push(ApprovalsReviewStatus.PENDING);
            workflowStatuses.push(ApprovalWorkflowExecutionStatus.PENDING);
            break;
          case 'APPROVED':
            reviewStatuses.push(ApprovalsReviewStatus.APPROVED);
            workflowStatuses.push(ApprovalWorkflowExecutionStatus.PENDING);
            break;
          case 'COMPLETED':
            reviewStatuses.push(ApprovalsReviewStatus.APPROVED);
            workflowStatuses.push(ApprovalWorkflowExecutionStatus.SCHEDULED, ApprovalWorkflowExecutionStatus.COMPLETED);
            break;
          case 'DECLINED':
            reviewStatuses.push(ApprovalsReviewStatus.DECLINED);
            workflowStatuses.push(ApprovalWorkflowExecutionStatus.PENDING);
            break;
          case 'FAILED':
            reviewStatuses.push(ApprovalsReviewStatus.APPROVED);
            workflowStatuses.push(ApprovalWorkflowExecutionStatus.FAILED);
            break;
          default:
            break;
        }
      });

      // Remove duplicates
      const uniqueReviewStatuses = [...new Set(reviewStatuses)];
      const uniqueWorkflowStatuses = [...new Set(workflowStatuses)];

      if (uniqueReviewStatuses.length > 0) {
        filters.push(`reviewStatus anyOf ${JSON.stringify(uniqueReviewStatuses)}`);
      }
      if (uniqueWorkflowStatuses.length > 0) {
        filters.push(`status anyOf ${JSON.stringify(uniqueWorkflowStatuses)}`);
      }
    }

    if (appliedFilters.resourceId) {
      filters.push(`resourceId equals ${appliedFilters.resourceId}`);
    } else if (appliedFilters.projectKey) {
      filters.push(`resourceId equals proj/${appliedFilters.projectKey}`);
    }

    if (appliedFilters.resourceKind) {
      filters.push(`resourceKind equals ${JSON.stringify(appliedFilters.resourceKind)}`);
    }
  }

  if (filters.length > 0) {
    params.filter = filters.join(',');
  }

  return { ...params } as const;
}
